import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { RootState } from "store";
import ReasonType from "enums/ReasonType";
import { setPagination } from "slices/vocabularyFilters";
import LocationReason from "api/types/models/location_reason";
import { Pagination, PencilIcon, Table, Tag, Toggle } from "@mwi/ui";
import {
  useGetReasonsQuery,
  useGetSitesQuery,
  useUpdateReasonMutation,
} from "api";
import EditReasonVocabularyModal from "components/modals/admin/vocabulary/reason/edit";

const ReasonsVocabulary = (): JSX.Element => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { id } = useParams();

  const { filtering, pagination } = useSelector((state: RootState) => ({
    filtering: state.vocabularyFilters.filtering,
    pagination: state.vocabularyFilters.pagination,
  }));

  const { data: sites, isLoading: loadingSites } = useGetSitesQuery();

  const [updateReason] = useUpdateReasonMutation();

  const { data: reasons, isFetching: fetchingReasons } = useGetReasonsQuery(
    {
      page: pagination.page,
      size: pagination.size,
      locationUuid: filtering.location_uuid ?? undefined,
      search: filtering.search,
      includeDisabled: true,
    },
    { skip: !filtering?.location_uuid },
  );

  const handleReasonToggle = (reason: LocationReason, value: boolean) => {
    updateReason({
      id: reason.id,
      body: {
        is_active: value,
        translation: reason.translation,
        notification: reason.notification,
      },
    });
  };

  return (
    <>
      <div className="w-full overflow-x-auto">
        <Table loading={fetchingReasons || loadingSites}>
          <Table.Head>
            <Table.Row>
              <Table.Item as="th">Reason</Table.Item>
              <Table.Item as="th">Translation</Table.Item>
              <Table.Item as="th">Location</Table.Item>
              <Table.Item as="th">Notification</Table.Item>
              <Table.Item as="th">Duration</Table.Item>
              <Table.Item as="th">Active</Table.Item>
              <Table.Item as="th">Edit</Table.Item>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {reasons && reasons?.data?.length ? (
              reasons.data.map((reason) => {
                const site = sites?.find((s) => s.id === reason.location_uuid);

                return (
                  <Table.Row key={reason.id}>
                    <Table.Item shouldWrap>{reason.name}</Table.Item>
                    <Table.Item>
                      {reason.translation ? (
                        reason.translation
                      ) : (
                        <Tag variants="outlined">N/A</Tag>
                      )}
                    </Table.Item>
                    <Table.Item>
                      {site?.name ? (
                        <Tag>{site.name}</Tag>
                      ) : (
                        <Tag variants="outlined">N/A</Tag>
                      )}
                    </Table.Item>
                    <Table.Item>
                      {(!reason?.notification ||
                        !reason?.notification?.is_active) && (
                        <Tag variants="outlined">N/A</Tag>
                      )}
                      {reason?.notification?.type === ReasonType.EMERGENCY &&
                        reason?.notification?.is_active && (
                          <Tag variants="danger">Emergency</Tag>
                        )}
                      {reason.notification?.type === ReasonType.NOTIFICATION &&
                        reason?.notification?.is_active && (
                          <Tag variants="success">Notification</Tag>
                        )}
                    </Table.Item>
                    <Table.Item>
                      {reason.duration_minutes ? reason.duration_minutes : 0}
                    </Table.Item>
                    <Table.Item>
                      <Toggle
                        disabled={!reason.duration_minutes}
                        value={reason.enabled}
                        onChange={(value) => handleReasonToggle(reason, value)}
                      />
                    </Table.Item>
                    <Table.Item>
                      <button
                        type="button"
                        className={classNames("inline-block text-primary", {
                          "!text-grey-dark !cursor-default":
                            !reason.duration_minutes,
                        })}
                        disabled={!reason.duration_minutes}
                        onClick={() =>
                          navigate(
                            `/admin/customisations/vocabulary/${reason.id}`,
                          )
                        }
                      >
                        <PencilIcon className="w-6 h-6 cursor-pointer pointer-events-none" />
                      </button>
                    </Table.Item>
                  </Table.Row>
                );
              })
            ) : (
              <Table.Row>
                <Table.Item colSpan={7} className="text-center">
                  No reasons found
                </Table.Item>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </div>

      <div className="mt-4 col-span-full">
        <Pagination
          onPrevPage={() =>
            dispatch(setPagination({ page: pagination.page - 1 }))
          }
          prevPage={pagination.page > 1}
          currentPage={pagination.page}
          nextPage={reasons?.meta?.has_next_page ?? false}
          onNextPage={() =>
            dispatch(setPagination({ page: pagination.page + 1 }))
          }
          loading={fetchingReasons}
        />
      </div>

      <EditReasonVocabularyModal
        isOpen={!!id && filtering?.category === "reasons"}
        onClose={() => navigate("/admin/customisations/vocabulary")}
      />
    </>
  );
};

export default ReasonsVocabulary;
